/**
 * Chiamate alle API dell'uscita su elenco dei siti ospitati (ADR 0039 §3).
 *
 * Un sito è aperto (esce verso internet, com'era prima) o chiuso (esce solo verso i
 * domini dell'elenco). I siti nuovi nascono chiusi; quelli già pubblicati restano
 * aperti e per due settimane la piattaforma registra i domini che contattano. I domini
 * li aggiunge solo il proprietario del set o un amministratore: l'Institore propone.
 *
 * Provvisorio dichiarato: percorsi e tipi scritti a mano (CLAUDE.md §2.3), presidiati da
 * `siteEgress.test.ts`.
 */

import type { ApiClient } from '../client';

/** Uscita del sito: aperta o chiusa sull'elenco. */
export type EgressMode = 'open' | 'closed';

/** Stato di un dominio nell'elenco. */
export type DomainState = 'allowed' | 'proposed' | 'observed' | 'rejected';

/** Da dove è arrivato un dominio. */
export type DomainSource = 'owner' | 'institore' | 'recording';

/** Codici dei rifiuti dell'elenco, tradotti dalla pagina. */
export const EGRESS_REFUSAL_CODES = [
  'invalid_domain',
  'internal_domain',
  'domain_limit',
  'domain_not_pending',
  'hosting_missing',
  'read_only_project',
] as const;

/** Un dominio dell'elenco. */
export interface SiteDomain {
  /** Dominio, anche `*.<dominio>`. */
  name: string;
  /** Stato. */
  state: DomainState;
  /** Prima provenienza. */
  source: DomainSource;
  /** Motivo dell'Institore: testo semplice, da mostrare come dato. */
  reason: string;
  /** Risposte DNS registrate. */
  hits: number;
  /** Prima risposta registrata, ISO 8601. */
  first_seen_at: string | null;
  /** Ultima risposta registrata, ISO 8601. */
  last_seen_at: string | null;
  /** Ingresso nell'elenco, ISO 8601. */
  created_at: string;
}

/** Uscita del sito di un progetto. */
export interface SiteEgress {
  /** Aperta o chiusa. */
  mode: EgressMode;
  /** True se la piattaforma applica l'elenco. */
  enforced: boolean;
  /** True se la registrazione dei domini è in corso. */
  recording: boolean;
  /** True se la registrazione ha una fonte; false: i domini si scrivono a mano. */
  recorder: boolean;
  /** Fine della registrazione, ISO 8601; null per un sito nato chiuso. */
  recording_until: string | null;
  /** Chiusura, ISO 8601; null se aperta. */
  closed_at: string | null;
  /** Elenco dei domini. */
  domains: SiteDomain[];
}

/**
 * Percorso delle rotte dell'uscita del sito.
 *
 * @param setId Set.
 * @param projectId Progetto.
 * @param suffix Parte finale.
 * @returns Il percorso.
 */
function egressPath(setId: string, projectId: string, suffix = ''): string {
  return `/api/v1/agent-sets/${encodeURIComponent(setId)}/projects/${encodeURIComponent(projectId)}/hosting/egress${suffix}`;
}

/** Operazioni sull'uscita dei siti, esposte alla UI. */
export class SiteEgressApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Legge l'uscita del sito di un progetto del set.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @returns Lo stato con l'elenco.
   * @throws ApiError 404 se il set non contiene il progetto o il sito non c'è.
   */
  get(setId: string, projectId: string): Promise<SiteEgress> {
    return this.client.request<SiteEgress>('GET', egressPath(setId, projectId));
  }

  /**
   * Aggiunge un dominio all'elenco.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param name Dominio.
   * @returns Lo stato dopo l'aggiunta.
   * @throws ApiError 422 `invalid_domain` o `internal_domain`, 409 `domain_limit`.
   */
  addDomain(setId: string, projectId: string, name: string): Promise<SiteEgress> {
    return this.client.request<SiteEgress>('POST', egressPath(setId, projectId, '/domains'), { name });
  }

  /**
   * Toglie un dominio dall'elenco.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param name Dominio.
   * @returns Lo stato dopo la rimozione.
   */
  removeDomain(setId: string, projectId: string, name: string): Promise<SiteEgress> {
    return this.client.request<SiteEgress>(
      'DELETE',
      egressPath(setId, projectId, `/domains/${encodeURIComponent(name)}`),
    );
  }

  /**
   * Rifiuta una proposta o un dominio registrato.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param name Dominio.
   * @returns Lo stato dopo il rifiuto.
   * @throws ApiError 409 `domain_not_pending` per un dominio già ammesso.
   */
  rejectDomain(setId: string, projectId: string, name: string): Promise<SiteEgress> {
    return this.client.request<SiteEgress>(
      'POST',
      egressPath(setId, projectId, `/domains/${encodeURIComponent(name)}/reject`),
    );
  }

  /**
   * Conferma l'elenco e chiude l'uscita del sito.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param domains Domini confermati.
   * @returns Lo stato dopo la chiusura.
   */
  close(setId: string, projectId: string, domains: string[]): Promise<SiteEgress> {
    return this.client.request<SiteEgress>('POST', egressPath(setId, projectId, '/close'), { domains });
  }
}
